import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import DatePicker from 'react-datepicker';
import ReactPaginate from 'react-paginate';
import { getActiveAdvertisements } from '../../api/advertisementService';
import 'react-datepicker/dist/react-datepicker.css';

const Advertisement = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const searchInputRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  useEffect(() => {
    if (searchTerm && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
      const length = searchInputRef.current.value.length;
      searchInputRef.current.setSelectionRange(length, length);
    }
  }, [searchTerm]);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getActiveAdvertisements();

      if (response.isSuccess && response.data) {
        setAdvertisements(response.data.items ?? []);
      } else {
        setError('İlanlar yüklenirken bir hata oluştu');
      }
    } catch {
      setError('İlanlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy');
    } catch {
      return dateString;
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(0);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(0);
  };

  const filteredAdvertisements = advertisements.filter((advertisement) => {
    const header = advertisement.header?.toLowerCase() ?? '';
    const department = advertisement.department?.toLowerCase() ?? '';
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch = header.includes(normalizedSearch) || department.includes(normalizedSearch);

    if (startDate && endDate) {
      const advertisementDate = new Date(advertisement.date);
      return matchesSearch && advertisementDate >= startDate && advertisementDate <= endDate;
    }

    return matchesSearch;
  });

  const pageCount = Math.ceil(filteredAdvertisements.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentAdvertisements = filteredAdvertisements.slice(offset, offset + itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };

  const actionBodyTemplate = (rowData) => (
    <Link
      to={`/ilanlar/${rowData.urlTag}`}
      className="btn btn-primary btn-sm"
      state={{ guid: rowData.guid }}
    >
      Detaya Git
    </Link>
  );

  if (loading) {
    return (
      <div className="council-decisions">
        <div className="container">
          <h1>İlanlar</h1>
          <div className="text-center py-5">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="council-decisions">
        <div className="container">
          <h1>İlanlar</h1>
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="council-decisions">
      <Helmet>
        <title>İlanlar | İskenderun Belediyesi</title>
        <meta name="description" content="İskenderun Belediyesi ilanlar listesi." />
      </Helmet>

      <div className="container">
        <h1>İlanlar</h1>

        <div className="news-tabs-filter">
          <div className="news-tabs-filter-left">
            <div className="news-tabs-filter-left-item">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="İlanlarda Ara"
                value={searchTerm}
                onChange={handleSearch}
              />
              <i className="pi pi-search"></i>
            </div>
          </div>

          <div className="news-tabs-filter-right">
            <div className="news-tabs-filter-right-item">
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                locale={tr}
                className="form-control"
                placeholderText="Tarih Aralığı"
                dateFormat="dd.MM.yyyy"
                isClearable
              />
              <i className="pi pi-calendar"></i>
            </div>
          </div>
        </div>

        <DataTable value={currentAdvertisements} className="council-table">
          <Column
            field="date"
            header="Tarih"
            style={{ width: '15%' }}
            body={(rowData) => formatDate(rowData.date)}
          />
          <Column field="header" header="İlan Başlığı" style={{ width: '45%' }} />
          <Column field="department" header="Birim" style={{ width: '25%' }} />
          <Column body={actionBodyTemplate} header="" style={{ width: '15%' }} />
        </DataTable>

        <ReactPaginate
          previousLabel="<"
          nextLabel=">"
          breakLabel="..."
          pageCount={pageCount}
          forcePage={currentPage}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          breakClassName="page-item"
          breakLinkClassName="page-link"
          activeClassName="active"
        />
      </div>
    </div>
  );
};

export default Advertisement;
